import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from "react";
import type { AuthState } from "@/types/auth";
import * as authService from "@/services/auth.service";
import { flowStep, mark, measure } from "@/lib/perf";

interface AuthContextValue extends AuthState {
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        practiceId: null,
        loading: true,
        error: null,
    });

    // Track if login() is actively running to avoid race with onAuthStateChange
    const loginInProgress = useRef(false);

    // Session restore on mount
    useEffect(() => {
        let mounted = true;

        async function restore() {
            const user = await authService.restoreSession();
            if (mounted) {
                setState({
                    user,
                    practiceId: user?.practiceId || null,
                    loading: false,
                    error: null,
                });
            }
        }

        restore();

        // Listen for auth state changes (token refresh, external logout)
        const {
            data: { subscription },
        } = authService.onAuthStateChange((user, meta) => {
            // Skip if login() is handling state itself to avoid race condition
            if (loginInProgress.current) return;
            if (!user && !meta?.shouldClearAuthState) return;
            if (mounted) {
                setState({
                    user,
                    practiceId: user?.practiceId || null,
                    loading: false,
                    error: null,
                });
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const login = useCallback(async (email: string, password: string) => {
        loginInProgress.current = true;
        setState((s) => ({ ...s, loading: true, error: null }));

        mark('auth_context_login_start');
        flowStep('login', 'auth_context_login_start');

        try {
            const result = await authService.login({ email, password });
            mark('auth_context_login_end');
            measure('auth_context_login_ms', 'auth_context_login_start', 'auth_context_login_end');

            if (result.success && result.user) {
                setState({
                    user: result.user,
                    practiceId: result.user.practiceId || null,
                    loading: false,
                    error: null,
                });
                flowStep('login', 'session_set', { role: result.user.role });
                return { success: true };
            }

            flowStep('login', 'auth_context_login_failed', {
                reason: result.error || 'unknown_auth_error',
            });

            setState((s) => ({
                ...s,
                loading: false,
                error: result.error || "Anmeldung fehlgeschlagen.",
            }));
            return { success: false, error: result.error };
        } finally {
            // Allow onAuthStateChange to handle future events again
            setTimeout(() => {
                loginInProgress.current = false;
            }, 500);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await authService.logout();
        } catch {
            // Even if remote signOut fails, clear local auth state to avoid stuck sessions.
        } finally {
            setState({ user: null, practiceId: null, loading: false, error: null });
        }
    }, []);

    return (
        <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
